(() => {
  const configElement = document.querySelector("[data-shipping-protection-config]");
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent || "{}");
  } catch (error) {
    console.error("Shipping Protection configuration could not be read.", error);
    return;
  }

  const variants = Array.isArray(config.variants) ? config.variants.map((variant) => ({
    id: Number(variant.id || 0),
    price: Number(variant.price || 0),
    available: Boolean(variant.available),
  })).filter((variant) => variant.id > 0 && variant.price > 0) : [];
  const variantIds = new Set(variants.map((variant) => variant.id));
  let operation = Promise.resolve();

  const isConfigured = () => Boolean(config.enabled && Number(config.productId || 0) > 0 && variants.length);
  const isProtectionItem = (item) => Number(item?.product_id || item?.productId || 0) === Number(config.productId || 0)
    || variantIds.has(Number(item?.variant_id || item?.id || 0));
  const getItems = (cart) => Array.isArray(cart?.items) ? cart.items : [];
  const getProtectionItems = (cart) => getItems(cart).filter(isProtectionItem);
  const isEnabledInCart = (cart) => getProtectionItems(cart).length > 0;
  const getEligibleSubtotal = (cart) => getItems(cart).reduce((subtotal, item) => {
    if (isProtectionItem(item)) return subtotal;
    return subtotal + Math.max(0, Number(item.final_line_price ?? item.line_price ?? 0));
  }, 0);

  const calculatePrice = (eligibleSubtotalCents) => {
    const subtotal = Math.max(0, Math.round(Number(eligibleSubtotalCents || 0)));
    if (!subtotal) return 0;
    const basisPoints = Math.max(0, Math.round(Number(config.percentageBasisPoints || 300)));
    const increment = Math.max(1, Math.round(Number(config.roundingIncrementCents || 500)));
    const minimum = Math.max(0, Math.round(Number(config.minimumChargeCents || 1500)));
    const roundedCharge = Math.ceil((subtotal * basisPoints) / (10000 * increment)) * increment;
    return Math.max(minimum, roundedCharge);
  };

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(config.locale || "en-GB", {
        style: "currency",
        currency: config.currency || "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      return `£${amount.toFixed(2)}`;
    }
  };

  const getTargetVariant = (cart) => {
    const targetPrice = calculatePrice(getEligibleSubtotal(cart));
    if (!targetPrice) return { targetPrice: 0, variant: null };
    return {
      targetPrice,
      variant: variants.find((variant) => variant.available && variant.price === targetPrice) || null,
    };
  };

  const fetchCart = async () => {
    const response = await fetch("/cart.js", { headers: { Accept: "application/json" }, cache: "no-store" });
    if (!response.ok) throw new Error("Unable to load the basket.");
    return response.json();
  };

  const cartRequest = async (url, body) => {
    const response = await fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const responseBody = await response.json().catch(() => ({}));
      throw new Error(responseBody.description || responseBody.message || "Unable to update Shipping Protection.");
    }
    return response.json();
  };

  const removeProtectionItems = async (cart) => {
    const protectionItems = getProtectionItems(cart);
    let updatedCart = cart;
    for (const item of protectionItems) {
      updatedCart = await cartRequest("/cart/change.js", { id: item.key, quantity: 0 });
    }
    return updatedCart;
  };

  const reconcile = async (providedCart, desiredEnabled) => {
    if (!isConfigured()) return providedCart || fetchCart();
    let cart = providedCart || await fetchCart();
    const protectionItems = getProtectionItems(cart);

    if (!desiredEnabled || getEligibleSubtotal(cart) <= 0) {
      return protectionItems.length ? removeProtectionItems(cart) : cart;
    }

    const { targetPrice, variant } = getTargetVariant(cart);
    if (!variant) {
      if (protectionItems.length) await removeProtectionItems(cart);
      throw new Error(`The ${formatMoney(targetPrice)} Shipping Protection tier is unavailable.`);
    }

    const correctItems = protectionItems.filter((item) => Number(item.variant_id || item.id) === variant.id);
    const isAlreadyCorrect = protectionItems.length === 1
      && correctItems.length === 1
      && Number(correctItems[0].quantity) === 1;
    if (isAlreadyCorrect) return cart;

    if (protectionItems.length) cart = await removeProtectionItems(cart);
    return cartRequest("/cart/add.js", {
      items: [{
        id: variant.id,
        quantity: 1,
        properties: { _shipping_protection: "true" },
      }],
    }).then(fetchCart);
  };

  const queue = (task) => {
    const nextOperation = operation.catch(() => undefined).then(task);
    operation = nextOperation;
    return nextOperation;
  };

  const setCardLoading = (loading) => {
    document.querySelectorAll("[data-shipping-protection-card]").forEach((card) => {
      card.classList.toggle("is-loading", loading);
      card.setAttribute("aria-busy", String(loading));
      const toggle = card.querySelector("[data-shipping-protection-toggle]");
      if (toggle) toggle.disabled = loading || card.dataset.protectionAvailable !== "true";
    });
  };

  const setStatus = (message = "", isError = false) => {
    document.querySelectorAll("[data-shipping-protection-status]").forEach((status) => {
      status.textContent = message;
      status.classList.toggle("is-error", isError);
      status.classList.toggle("sr-only", !isError);
    });
  };

  const updateCards = (cart) => {
    if (!isConfigured() || !cart) return;
    const eligibleSubtotal = getEligibleSubtotal(cart);
    const active = isEnabledInCart(cart);
    const { targetPrice, variant } = getTargetVariant(cart);

    document.querySelectorAll("[data-shipping-protection-card]").forEach((card) => {
      card.hidden = eligibleSubtotal <= 0;
      card.classList.toggle("is-active", active);
      card.dataset.protectionAvailable = String(eligibleSubtotal > 0 && Boolean(variant));
      const price = card.querySelector("[data-shipping-protection-price]");
      const toggle = card.querySelector("[data-shipping-protection-toggle]");
      if (price) price.textContent = targetPrice ? formatMoney(targetPrice) : formatMoney(0);
      if (toggle) {
        toggle.setAttribute("aria-checked", String(active));
        toggle.disabled = eligibleSubtotal <= 0 || !variant;
      }
      const status = card.querySelector("[data-shipping-protection-status]");
      if (!variant && eligibleSubtotal > 0) {
        if (status) {
          status.textContent = `${formatMoney(targetPrice)} tier unavailable.`;
          status.classList.add("is-error");
          status.classList.remove("sr-only");
        }
      } else if (status?.classList.contains("is-error")) {
        status.textContent = "";
        status.classList.remove("is-error");
        status.classList.add("sr-only");
      }
    });
  };

  const stateSignature = (cart) => getProtectionItems(cart)
    .map((item) => `${item.key}:${item.variant_id || item.id}:${item.quantity}`)
    .sort()
    .join("|");

  const setEnabled = (desiredEnabled) => queue(async () => {
    setCardLoading(true);
    setStatus(desiredEnabled ? "Adding protection…" : "Removing protection…");
    try {
      const currentCart = await fetchCart();
      const updatedCart = await reconcile(currentCart, desiredEnabled);
      updateCards(updatedCart);
      setStatus(desiredEnabled ? "Shipping Protection added." : "Shipping Protection removed.");
      document.dispatchEvent(new CustomEvent("quantum:shipping-protection-updated", {
        detail: { cart: updatedCart, source: "toggle" },
      }));
      return updatedCart;
    } catch (error) {
      console.error("Shipping Protection update failed.", error);
      const restoredCart = await fetchCart().catch(() => null);
      if (restoredCart) updateCards(restoredCart);
      setStatus(error.message || "Unable to update protection. Please try again.", true);
      throw error;
    } finally {
      setCardLoading(false);
    }
  });

  const changeItemAndReconcile = (key, quantity) => queue(async () => {
    setCardLoading(true);
    try {
      const currentCart = await fetchCart();
      const protectionWasEnabled = isEnabledInCart(currentCart);
      const changedItemIsProtection = getProtectionItems(currentCart).some((item) => item.key === key);
      const changedCart = await cartRequest("/cart/change.js", {
        id: key,
        quantity: Math.max(0, Math.round(Number(quantity || 0))),
      });
      return reconcile(changedCart, protectionWasEnabled && !changedItemIsProtection);
    } finally {
      setCardLoading(false);
    }
  });

  const updateCartPage = (form, submitter) => queue(async () => {
    const controls = [...form.querySelectorAll("button, input")];
    controls.forEach((control) => { control.disabled = true; });
    setCardLoading(true);
    setStatus("Updating your basket…");
    try {
      const updates = {};
      form.querySelectorAll("[data-cart-page-key]").forEach((input) => {
        updates[input.dataset.cartPageKey] = Math.max(0, Math.round(Number(input.value || 0)));
      });
      let cart = await cartRequest("/cart/update.js", { updates });
      cart = await reconcile(cart, true);
      document.dispatchEvent(new CustomEvent("quantum:shipping-protection-updated", {
        detail: { cart, source: "cart-form" },
      }));
      window.location.href = submitter?.name === "checkout" ? "/checkout" : (config.cartUrl || "/cart");
    } catch (error) {
      console.error("Basket and Shipping Protection update failed.", error);
      const restoredCart = await fetchCart().catch(() => null);
      if (restoredCart) updateCards(restoredCart);
      setStatus(error.message || "Unable to update your basket. Please try again.", true);
      controls.forEach((control) => { control.disabled = false; });
      setCardLoading(false);
    }
  });

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-shipping-protection-toggle]");
    if (!toggle || toggle.disabled) return;
    const shouldEnable = toggle.getAttribute("aria-checked") !== "true";
    setEnabled(shouldEnable).catch(() => undefined);
  });

  document.addEventListener("submit", (event) => {
    const form = event.target.closest("[data-cart-page-form]");
    if (!form || !isConfigured()) return;
    const toggle = form.querySelector('[data-shipping-protection-toggle][aria-checked="true"]');
    if (!toggle) return;
    event.preventDefault();
    updateCartPage(form, event.submitter);
  });

  document.addEventListener("quantum:cart-rendered", (event) => updateCards(event.detail?.cart));
  document.addEventListener("quantum:shipping-protection-updated", (event) => updateCards(event.detail?.cart));
  document.addEventListener("quantum:shipping-protection-updated", (event) => {
    if (!document.querySelector("[data-main-cart-section]")) return;
    if (event.detail?.source !== "toggle" && event.detail?.source !== "sync") return;
    window.location.reload();
  });

  window.QuantumShippingProtection = {
    config,
    calculatePrice,
    changeItemAndReconcile,
    fetchCart,
    formatMoney,
    getEligibleSubtotal,
    getProtectionItems,
    getTargetVariant,
    isConfigured,
    isEnabledInCart,
    isProtectionItem,
    reconcile: (cart, desiredEnabled) => queue(() => reconcile(cart, desiredEnabled)),
    setEnabled,
    stateSignature,
    updateCards,
  };
})();
